import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { HighLevelContext as HighLevelContextType } from '@/types';
import { initHighLevelListener, getHighLevelContext } from '@/utils/highlevel';

interface HighLevelContextValue {
  context: HighLevelContextType | null;
  isLoading: boolean;
  error: string | null;
}

const HighLevelContextContext = createContext<HighLevelContextValue>({
  context: null,
  isLoading: true,
  error: null,
});

export function useHighLevelContext() {
  return useContext(HighLevelContextContext);
}

interface HighLevelProviderProps {
  children: ReactNode;
}

export function HighLevelProvider({ children }: HighLevelProviderProps) {
  const [context, setContext] = useState<HighLevelContextType | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // Check for existing context first
    const existingContext = getHighLevelContext();
    if (existingContext) {
      console.log('Using existing HighLevel context:', existingContext);
      setContext(existingContext);
      setIsLoading(false);
      return;
    }

    // Set up listener for HighLevel context
    const cleanup = initHighLevelListener((newContext) => {
      console.log('HighLevel context received via postMessage:', newContext);
      setContext(newContext);
      setIsLoading(false);
      setError(null);
    });

    // Set a timeout for context loading
    const timeout = setTimeout(() => {
      if (!context) {
        console.warn('Timeout waiting for HighLevel context');
        setIsLoading(false);
        setError('Unable to load context. Please refresh the page or contact support.');
      }
    }, 10000); // 10 second timeout

    return () => {
      cleanup();
      clearTimeout(timeout);
    };
  }, []);

  return (
    <HighLevelContextContext.Provider value={{ context, isLoading, error }}>
      {children}
    </HighLevelContextContext.Provider>
  );
}

// Loading component shown while waiting for context
export function HighLevelLoadingScreen() {
  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center">
      <div className="text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4"></div>
        <p className="text-gray-600">Loading...</p>
      </div>
    </div>
  );
}

// Error component shown when context fails to load
export function HighLevelErrorScreen({ error }: { error: string }) {
  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center">
      <div className="text-center max-w-md mx-auto p-6">
        <div className="text-red-500 text-5xl mb-4">⚠️</div>
        <h1 className="text-xl font-semibold text-gray-900 mb-2">Connection Error</h1>
        <p className="text-gray-600 mb-4">{error}</p>
        <button
          onClick={() => window.location.reload()}
          className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 transition-colors"
        >
          Refresh Page
        </button>
      </div>
    </div>
  );
}
